import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { celebrate } from "./celebrate";
import { DISCOUNT_TIERS, MOQ, getDiscountPct } from "./discountTiers";
import { useAuth } from "./auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hybrid cart: anonymous via localStorage, persists to backend `user_carts`
// once the user logs in. On login, the local cart is MERGED (union by
// product_id, summing qty) with the server cart, then synced both ways.

const STORAGE_KEY = "wfw_cart_v1";

const CartContext = createContext(null);

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

const writeStored = (items) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
};

const compute = (items) => {
  const total_qty = items.reduce((s, i) => s + i.qty, 0);
  const pct = getDiscountPct(total_qty);
  const lines = items.map((i) => ({
    ...i,
    discount_pct: pct,
    line_total: +(i.qty * i.unit_price * (1 - pct / 100)).toFixed(2),
  }));
  const subtotal = +lines.reduce((s, l) => s + l.line_total, 0).toFixed(2);
  return {
    items: lines,
    subtotal,
    total_qty,
    cart_discount_pct: pct,
    moq: MOQ,
    moq_reached: total_qty >= MOQ,
    moq_remaining: Math.max(0, MOQ - total_qty),
  };
};

const tierForPct = (pct) => DISCOUNT_TIERS.find((t) => t.pct === pct);

const fireUnlocks = (prevQty, nextQty) => {
  const prevPct = getDiscountPct(prevQty);
  const nextPct = getDiscountPct(nextQty);
  if (nextPct > prevPct) {
    const tier = tierForPct(nextPct);
    celebrate({
      emoji: tier?.tone || "🎉",
      title: `${tier?.label || "Reward"} unlocked!`,
      subtitle: `${nextPct}% off applied to your cart`,
    });
    return;
  }
  // MOQ unlock celebration
  if (prevQty < MOQ && nextQty >= MOQ) {
    celebrate({
      emoji: "🛒",
      title: "Checkout unlocked!",
      subtitle: `Minimum order reached (${MOQ} items)`,
    });
  }
};

export const CartProvider = ({ children }) => {
  const [raw, setRaw] = useState(readStored);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, ready } = useAuth();
  const lastSyncedUserId = useRef(null);

  useEffect(() => { writeStored(raw); }, [raw]);

  // ─── Merge guest → user cart on login ────────────────────────────
  useEffect(() => {
    if (!ready || !user || lastSyncedUserId.current === user.id) return;
    lastSyncedUserId.current = user.id;

    const merge = async () => {
      try {
        const local = readStored();
        const { data } = await axios.post(`${API}/cart/merge`, {
          items: local.map((i) => ({
            product_id: i.product_id,
            name: i.name,
            slug: i.slug,
            image: i.image,
            unit_price: i.unit_price,
            qty: i.qty,
          })),
        });
        // Replace local with server-merged
        setRaw(data.items || []);
        if (local.length > 0 && data.items?.length > 0) {
          celebrate({
            emoji: "🛒",
            title: "Cart synced!",
            subtitle: `${data.items.length} item${data.items.length === 1 ? "" : "s"} restored to your account`,
          });
        }
      } catch (e) {
        // silent — user not auth or network failed
      }
    };
    merge();
  }, [ready, user]);

  // ─── Push cart mutations to backend (debounced) when user is logged in ───
  useEffect(() => {
    if (!user) return;
    const handle = setTimeout(() => {
      axios.post(`${API}/cart/save`, {
        items: raw.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          slug: i.slug,
          image: i.image,
          unit_price: i.unit_price,
          qty: i.qty,
        })),
      }).catch(() => { /* silent */ });
    }, 800);
    return () => clearTimeout(handle);
  }, [raw, user]);

  // ─── Reset local cart on logout ──────────────────────────────────
  useEffect(() => {
    if (ready && !user && lastSyncedUserId.current) {
      // user logged out — keep local cart as is (it's saved in localStorage)
      lastSyncedUserId.current = null;
    }
  }, [ready, user]);

  const addItem = useCallback((product, qty = 1) => {
    setRaw((items) => {
      const prevTotalQty = items.reduce((s, i) => s + i.qty, 0);
      let found = false;
      const next = items.map((i) => {
        if (i.product_id === product.id) {
          found = true;
          return { ...i, qty: i.qty + qty };
        }
        return i;
      });
      if (!found) {
        next.push({
          product_id: product.id,
          name: product.name,
          slug: product.slug || null,
          image: (product.images && product.images[0]) || product.image || null,
          unit_price: Number(product.price),
          qty,
        });
      }
      const newTotalQty = next.reduce((s, i) => s + i.qty, 0);
      fireUnlocks(prevTotalQty, newTotalQty);
      return next;
    });
    setDrawerOpen(true);
  }, []);

  const updateItem = useCallback((product_id, qty) => {
    setRaw((items) => {
      const prevTotalQty = items.reduce((s, i) => s + i.qty, 0);
      const next = qty <= 0
        ? items.filter((i) => i.product_id !== product_id)
        : items.map((i) => (i.product_id === product_id ? { ...i, qty } : i));
      const newTotalQty = next.reduce((s, i) => s + i.qty, 0);
      fireUnlocks(prevTotalQty, newTotalQty);
      return next;
    });
  }, []);

  const removeItem = useCallback((product_id) => {
    setRaw((items) => items.filter((i) => i.product_id !== product_id));
  }, []);

  const clear = useCallback(() => setRaw([]), []);

  const cart = compute(raw);

  return (
    <CartContext.Provider
      value={{
        cart,
        loading: false,
        addItem,
        updateItem,
        removeItem,
        clear,
        drawerOpen,
        openDrawer: () => setDrawerOpen(true),
        closeDrawer: () => setDrawerOpen(false),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
